/**
 * Relatórios (Legado): panorama dos ambientes EM ABERTO da planilha
 * Gerenciador de Produção, por cliente ou por pedido.
 *
 * Substitui o relatório impresso que a equipe entregava ao dono ("Ambientes
 * aguardando carregamento").
 *
 * Desenho (refeito em 02/10/2026 depois da crítica do dono à 1ª versão, um
 * mural de cartões coloridos): é uma LISTA limpa. Uma linha por
 * cliente/pedido, colunas alinhadas no desktop, blocos por estado ("Com
 * atraso", "Vencem em 7 dias", "Em dia"). O estado aparece UMA vez por
 * linha, num ponto de cor ao lado do texto; todo o resto é neutro. Fase é
 * texto e uma barra cinza, nunca cor.
 *
 * Só lê a planilha (`useDados`). As regras estão em `dominio.js` e
 * `filtros.js`, puras e testadas.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiChevronDown, FiSearch, FiSliders, FiX } from "react-icons/fi";

import AmbienteDetalhesDrawer from "../../components/AmbienteDetalhesDrawer";
import { EmptyStateErro } from "../../components/EmptyState";
import BotaoExportacao from "../../components/Exportacao";
import { BalaoComentarios } from "../../components/HistoricoAmbiente";
import { SoIcone } from "../../components/ListaCompacta/styles";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useDados } from "../../context/DadosContext";
import { useExportRender } from "../../context/ExportRenderContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import { formatarMoeda } from "../../utils/calculos";
import { plural } from "../../utils/dinheiro";
import { F } from "../../utils/gerencialFields";
import useProjetistasExecutivo from "../../hooks/useProjetistasExecutivo";
import { indiceDeIdentidade } from "../../utils/identidadeAmbiente";
import {
  SECOES,
  estadoDoGrupo,
  faixaComPendencia,
  faixaCurta,
  fasesComAmbiente,
  montarExportacao,
  selosDoAmbiente,
  textoPrazoAmbiente,
  textoSituacaoAmbiente,
  tomDaSituacao,
} from "./apresentacao";
import {
  AGRUPAMENTOS,
  ETAPAS,
  ORDENS,
  SITUACOES,
  agrupar,
  ambienteNaEtapa,
  ambienteNaSituacao,
  ambientesEmAberto,
  ordenarGrupos,
  resumir,
} from "./dominio";
import {
  alternarNaLista,
  aplicarFiltros,
  descreverFiltros,
  filtrosParaExportacao,
  limparFiltros,
} from "./filtros";
import PainelFiltros from "./PainelFiltros";
import {
  Ambientes,
  Aviso,
  BarraFases,
  Bloco,
  BlocoTitulo,
  BotaoFiltros,
  Busca,
  Cabecalho,
  Celula,
  ChipAtivo,
  ChipsAtivos,
  Controles,
  Datas,
  Estado,
  Fases,
  Indicador,
  Indicadores,
  Legenda,
  LinhaAmbiente,
  LinhaBusca,
  LinhaContagem,
  LinhaGrupo,
  LinhaTitulo,
  LinkLimpar,
  Lista,
  Pagina,
  Ponto,
  Quem,
  Segmentado,
  SeletorOrdem,
  Tag,
  TextoSituacao,
  Topo,
  Vazio,
} from "./styles";
import useEstadoRelatorio from "./useEstadoRelatorio";

export const PAINEL_EXPORTACAO = "relatorios-legado";

const INDICADORES = [
  { id: "atrasado", campo: "atrasados", rotulo: "Atrasados" },
  { id: "em_breve", campo: "emBreve", rotulo: "Vencem em 7 dias", curto: "Em 7 dias" },
  { id: "no_prazo", campo: "noPrazo", rotulo: "No prazo" },
  { id: "sem_prazo", campo: "semPrazo", rotulo: "Sem prazo" },
  { id: "bloqueado", campo: "bloqueados", rotulo: "Bloqueados" },
];

function Ambiente({ ambiente, onAbrir }) {
  return (
    <LinhaAmbiente>
      <button type="button" onClick={() => onAbrir(ambiente)}>
        <span className="nome" title={ambiente.ambiente}>
          {ambiente.ambiente}
          <BalaoComentarios row={ambiente.row} />
        </span>
        <span className="etapa">
          {ambiente.etapaLabel}
          {ambiente.lote ? ` · lote ${ambiente.lote}` : ""}
          {selosDoAmbiente(ambiente).map((selo) => (
            <Tag key={selo.id} $alerta={selo.id === "bloqueado"}>
              {selo.texto}
            </Tag>
          ))}
        </span>
        <span className="prazo">{textoPrazoAmbiente(ambiente)}</span>
        <TextoSituacao className="situacao" $tom={tomDaSituacao(ambiente.situacao)} $situacao={ambiente.situacao}>
          {textoSituacaoAmbiente(ambiente)}
        </TextoSituacao>
      </button>
    </LinhaAmbiente>
  );
}

function Grupo({ grupo, aberto, onAlternar, onAbrirAmbiente, precosVisiveis }) {
  const { resumo } = grupo;
  const estado = estadoDoGrupo(resumo);
  const fases = fasesComAmbiente(resumo);
  const idLista = `amb-${grupo.chave}`;
  const venda = faixaCurta(grupo.venda);
  const contrato = faixaCurta(grupo.entregaContrato);
  const carregamento = faixaComPendencia(grupo.carregamentoPrevisto, resumo.total);
  return (
    <LinhaGrupo data-grupo={grupo.chave}>
      <Cabecalho
        type="button"
        $comValor={precosVisiveis}
        aria-expanded={aberto}
        aria-controls={idLista}
        onClick={() => onAlternar(grupo.chave)}
      >
        <Quem>
          <strong title={grupo.titulo}>{grupo.titulo}</strong>
          <span title={grupo.subtitulo}>
            {grupo.subtitulo}
            {grupo.pedidoAmbiguo && (
              <Tag $alerta title="Este número de pedido aparece na planilha para mais de um cliente.">
                <FiAlertTriangle size={10} aria-hidden="true" /> pedido repetido
              </Tag>
            )}
          </span>
        </Quem>

        <Fases>
          <BarraFases aria-hidden="true">
            {fases.map((fase) => (
              <i key={fase.id} data-fase={fase.id} style={{ flexGrow: fase.total }} />
            ))}
          </BarraFases>
          <Legenda>
            {fases.map((fase, i) => (
              <React.Fragment key={fase.id}>
                {i > 0 ? " · " : ""}
                <span>
                  {fase.label} <b>{fase.total}</b>
                </span>
              </React.Fragment>
            ))}
          </Legenda>
        </Fases>

        <Estado>
          <span className="titulo">
            {estado.tom && <Ponto $tom={estado.tom} aria-hidden="true" />}
            {estado.titulo}
          </span>
          {estado.detalhe && <span className="detalhe">{estado.detalhe}</span>}
        </Estado>

        <Datas>
          <Celula data-rotulo="venda" $vazio={!venda}>
            {venda || "—"}
          </Celula>
          <Celula data-rotulo="contrato" $vazio={!contrato}>
            {contrato || "—"}
          </Celula>
          <Celula
            data-rotulo="carreg."
            $vazio={!grupo.carregamentoPrevisto.de}
            title={`${carregamento} (coluna DATA REPROGRAMADA da planilha)`}
          >
            {carregamento}
          </Celula>
          {precosVisiveis && (
            <Celula data-rotulo="valor" $numero>
              {formatarMoeda(resumo.valor)}
            </Celula>
          )}
        </Datas>

        <FiChevronDown className="seta" data-aberto={aberto ? "sim" : "nao"} size={16} aria-hidden="true" />
      </Cabecalho>

      {aberto && (
        <Ambientes id={idLista}>
          {grupo.ambientes.map((ambiente) => (
            <Ambiente key={ambiente.id} ambiente={ambiente} onAbrir={onAbrirAmbiente} />
          ))}
        </Ambientes>
      )}
    </LinhaGrupo>
  );
}

function contarPor(ambientes, itens, pertence) {
  return Object.fromEntries(itens.map((item) => [item.id, ambientes.filter((a) => pertence(a, item.id)).length]));
}

export default function Relatorios() {
  const { dados, erro, initialLoading } = useDados();
  const { precosVisiveis } = usePrecosVisiveis();
  const { renderAll } = useExportRender();
  const pageRef = useRef(null);
  const estado = useEstadoRelatorio();
  const { filtros, setFiltros, agrupamento, setAgrupamento, ordem, setOrdem } = estado;

  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [abertos, setAbertos] = useState(() => new Set());
  const [ambienteAberto, setAmbienteAberto] = useState(null);

  // Uma leitura da planilha por carga: status e prazos de todos os ambientes.
  // O projetista do executivo vem do backend; entra em cada ambiente para o filtro e a exportação.
  const { atribuicoes } = useProjetistasExecutivo();
  const universo = useMemo(
    () =>
      ambientesEmAberto(dados).map((ambiente) => ({
        ...ambiente,
        projetista: atribuicoes[ambiente.chave]?.projetista || "",
      })),
    [dados, atribuicoes],
  );
  const pedidosAmbiguos = useMemo(() => indiceDeIdentidade(dados).pedidosAmbiguos, [dados]);

  const filtrados = useMemo(() => aplicarFiltros(universo, filtros), [universo, filtros]);
  // Os números do topo ignoram o próprio filtro de situação: tocar em
  // "Atrasados" não pode zerar "No prazo", senão não há como trocar de um
  // para o outro. A linha de contagem logo abaixo diz o que a lista tem.
  const semSituacao = useMemo(
    () => aplicarFiltros(universo, filtros, { ignorar: "situacoes" }),
    [universo, filtros]
  );
  const resumoTopo = useMemo(() => resumir(semSituacao), [semSituacao]);
  const resumoLista = useMemo(() => resumir(filtrados), [filtrados]);

  const grupos = useMemo(
    () => ordenarGrupos(agrupar(filtrados, agrupamento, pedidosAmbiguos), ordem),
    [filtrados, agrupamento, pedidosAmbiguos, ordem]
  );

  // "Mais crítico" divide a lista em blocos por estado: o título do bloco diz
  // o estado uma vez. Nas outras ordens (nome, venda, entrega) os blocos
  // quebrariam a ordem pedida, então a lista fica corrida.
  const secoes = useMemo(() => {
    if (ordem !== "critico") return [{ id: "todos", titulo: null, grupos }];
    return SECOES.map((secao) => ({
      ...secao,
      grupos: grupos.filter((grupo) => estadoDoGrupo(grupo.resumo).secao === secao.id),
    })).filter((secao) => secao.grupos.length > 0);
  }, [grupos, ordem]);

  const contagemPorEtapa = useMemo(
    () => contarPor(aplicarFiltros(universo, filtros, { ignorar: "etapas" }), ETAPAS, ambienteNaEtapa),
    [universo, filtros]
  );
  const contagemPorSituacao = useMemo(
    () => contarPor(semSituacao, SITUACOES, ambienteNaSituacao),
    [semSituacao]
  );

  const chips = useMemo(() => descreverFiltros(filtros), [filtros]);
  // Só por pedido: é ali que o mesmo número vira duas linhas e pede explicação.
  // Por cliente, a marca na própria linha já avisa.
  const pedidosRepetidos = useMemo(
    () =>
      agrupamento === "pedido"
        ? [...new Set(grupos.filter((g) => g.pedidoAmbiguo && g.pedido).map((g) => g.pedido))].sort((a, b) =>
            a.localeCompare(b, "pt-BR", { numeric: true })
          )
        : [],
    [grupos, agrupamento]
  );

  const alternarGrupo = useCallback((chave) => {
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  }, []);

  const abrirAmbiente = useCallback((ambiente) => {
    setAmbienteAberto({
      ambiente: ambiente.row[F.AMBIENTE],
      lote: ambiente.row[F.NUMERO_DO_LOTE] ?? "—",
      row: ambiente.row,
    });
  }, []);

  const exportacao = useMemo(() => {
    const { resumo, secoes: secoesDoArquivo } = montarExportacao({
      grupos,
      resumoGeral: resumoLista,
      agrupamento,
      comValores: precosVisiveis,
    });
    return {
      titulo: `Relatório de ambientes em aberto por ${agrupamento === "pedido" ? "pedido" : "cliente"}`,
      // Gate do agendador: sem planilha carregada o snapshot sairia zerado.
      pronto: !initialLoading && !erro && dados.length > 0,
      resumo,
      secoes: secoesDoArquivo,
      visual: {
        ref: pageRef,
        sections: [
          { id: "resumo", label: "Resumo", selector: "[data-pdf='resumo-relatorio']" },
          { id: "lista", label: "Clientes/pedidos e ambientes", selector: "[data-pdf='lista-relatorio']" },
        ],
      },
    };
  }, [grupos, resumoLista, agrupamento, precosVisiveis, initialLoading, erro, dados.length]);
  const filtrosDoArquivo = useMemo(() => filtrosParaExportacao(filtros), [filtros]);

  useExportSnapshot(PAINEL_EXPORTACAO, exportacao, pageRef);

  if (initialLoading) return <PageLoader scope="relatorios" />;
  if (erro) return <EmptyStateErro erro={erro} />;

  const clientes = new Set(filtrados.map((a) => a.clienteChave)).size;
  const pedidos = new Set(filtrados.filter((a) => a.pedidoChave).map((a) => `${a.pedidoChave}|${a.clienteChave}`)).size;
  const rotuloGrupo = agrupamento === "pedido" ? "Pedido" : "Cliente";

  return (
    <Pagina ref={pageRef}>
      <Topo>
        <LinhaTitulo>
          <BlocoTitulo>
            <h1>Relatórios</h1>
            <p>Ambientes em aberto na planilha Gerenciador de Produção</p>
          </BlocoTitulo>
          <Controles>
            <Segmentado role="group" aria-label="Agrupar por">
              {AGRUPAMENTOS.map((opcao) => (
                <button
                  key={opcao.id}
                  type="button"
                  aria-pressed={agrupamento === opcao.id}
                  onClick={() => setAgrupamento(opcao.id)}
                >
                  {opcao.label}
                </button>
              ))}
            </Segmentado>
            <SeletorOrdem value={ordem} onChange={(e) => setOrdem(e.target.value)} aria-label="Ordenar por">
              {ORDENS.map((opcao) => (
                <option key={opcao.id} value={opcao.id}>
                  {opcao.label}
                </option>
              ))}
            </SeletorOrdem>
            <BotaoFiltros
              type="button"
              $ativo={chips.length > 0}
              aria-label={chips.length ? `Filtros (${chips.length} ativos)` : "Filtros"}
              onClick={() => setFiltrosAbertos(true)}
            >
              <FiSliders size={15} aria-hidden="true" />
              <span className="rotulo">Filtros</span>
              {chips.length > 0 && <span className="conta">{chips.length}</span>}
            </BotaoFiltros>
            <SoIcone>
              <BotaoExportacao
                titulo={exportacao.titulo}
                resumo={exportacao.resumo}
                secoes={exportacao.secoes}
                visual={exportacao.visual}
                filtrosAtivos={filtrosDoArquivo}
                label="Exportar"
              />
            </SoIcone>
          </Controles>
        </LinhaTitulo>

        <LinhaBusca>
          <Busca>
            <FiSearch size={14} aria-hidden="true" />
            <input
              type="search"
              value={filtros.busca}
              onChange={(e) => setFiltros((f) => ({ ...f, busca: e.target.value }))}
              placeholder="Cliente, pedido, ambiente ou lote"
              aria-label="Buscar cliente, pedido, ambiente ou lote"
            />
          </Busca>
          {chips.length > 0 && (
            <>
              <ChipsAtivos aria-label="Filtros ativos">
                {chips.map((chip) => (
                  <ChipAtivo
                    key={chip.id}
                    type="button"
                    title={`Remover filtro: ${chip.texto}`}
                    onClick={() => setFiltros((f) => chip.remover(f))}
                  >
                    <span>{chip.texto}</span>
                    <FiX size={12} aria-hidden="true" />
                  </ChipAtivo>
                ))}
              </ChipsAtivos>
              <LinkLimpar type="button" onClick={() => setFiltros((f) => limparFiltros(f))}>
                Limpar
              </LinkLimpar>
            </>
          )}
        </LinhaBusca>
      </Topo>

      <Indicadores data-pdf="resumo-relatorio" aria-label="Resumo">
        <Indicador as="div">
          <span className="num">{resumoTopo.total}</span>
          <span className="rot">Em aberto</span>
        </Indicador>
        {INDICADORES.map((indicador) => {
          const ligado = filtros.situacoes.includes(indicador.id);
          const valor = resumoTopo[indicador.campo];
          const tomDoPonto = tomDaSituacao(indicador.id);
          return (
            <Indicador
              key={indicador.id}
              type="button"
              $ativo={ligado}
              $zerado={valor === 0}
              aria-pressed={ligado}
              title={ligado ? `Tirar o filtro ${indicador.rotulo}` : `Ver só ${indicador.rotulo.toLowerCase()}`}
              onClick={() => setFiltros((f) => alternarNaLista(f, "situacoes", indicador.id))}
            >
              <span className="num">{valor}</span>
              <span className="rot">
                {valor > 0 && tomDoPonto !== "neutral" && <Ponto $tom={tomDoPonto} aria-hidden="true" />}
                {indicador.curto ? (
                  <>
                    <span className="longo">{indicador.rotulo}</span>
                    <span className="curto">{indicador.curto}</span>
                  </>
                ) : (
                  indicador.rotulo
                )}
              </span>
            </Indicador>
          );
        })}
      </Indicadores>

      <LinhaContagem>
        {`${plural(filtrados.length, "ambiente", "ambientes")} · ${plural(clientes, "cliente", "clientes")} · ${plural(
          pedidos,
          "pedido",
          "pedidos"
        )}`}
        {filtros.obraFutura ? " · com obra futura" : " · sem obra futura"}
        {precosVisiveis ? ` · ${formatarMoeda(resumoLista.valor)}` : ""}
      </LinhaContagem>

      {pedidosRepetidos.length > 0 && (
        <Aviso
          role="note"
          title="O mesmo número de pedido aparece na planilha para clientes diferentes. A lista completa está na página Início."
        >
          <FiAlertTriangle size={12} aria-hidden="true" />
          {` Pedido ${pedidosRepetidos.join(", ")}: mesmo número em mais de um cliente. Corrija na planilha.`}
        </Aviso>
      )}

      {grupos.length === 0 ? (
        <Vazio>
          {universo.length === 0 ? "Nenhum ambiente em aberto na planilha." : "Nenhum ambiente com esses filtros."}
          {universo.length > 0 && (
            <div>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setFiltros((f) => ({ ...limparFiltros(f), busca: "" }))}
              >
                Limpar filtros
              </button>
            </div>
          )}
        </Vazio>
      ) : (
        <div data-pdf="lista-relatorio" data-export-expand>
          {secoes.map((secao) => (
            <Bloco key={secao.id}>
              {secao.titulo && (
                <h2>
                  {secao.titulo} <span>{secao.grupos.length}</span>
                </h2>
              )}
              <Lista>
                <Cabecalho as="div" className="colunas" $comValor={precosVisiveis} aria-hidden="true">
                  <span>{rotuloGrupo}</span>
                  <span>Fases</span>
                  <span>Situação</span>
                  <span>Venda</span>
                  <span>Contrato</span>
                  <span>Carregamento</span>
                  {precosVisiveis && <span className="numero">Valor</span>}
                  <span />
                </Cabecalho>
                {secao.grupos.map((grupo) => (
                  <Grupo
                    key={grupo.chave}
                    grupo={grupo}
                    // Na captura para imagem todas as linhas saem abertas: o
                    // arquivo leva tudo o que está filtrado, não só o que foi tocado.
                    aberto={renderAll || abertos.has(grupo.chave)}
                    onAlternar={alternarGrupo}
                    onAbrirAmbiente={abrirAmbiente}
                    precosVisiveis={precosVisiveis}
                  />
                ))}
              </Lista>
            </Bloco>
          ))}
        </div>
      )}

      <PainelFiltros
        aberto={filtrosAbertos}
        onFechar={() => setFiltrosAbertos(false)}
        ambientes={universo}
        filtros={filtros}
        setFiltros={setFiltros}
        ordem={ordem}
        setOrdem={setOrdem}
        totalFiltrado={filtrados.length}
        contagemPorEtapa={contagemPorEtapa}
        contagemPorSituacao={contagemPorSituacao}
        visoes={estado.visoes}
        visaoAtivaId={estado.visaoAtivaId}
        onAplicarVisao={estado.aplicarVisao}
        onSalvarVisao={estado.salvarVisao}
        onExcluirVisao={estado.excluirVisao}
      />

      <AmbienteDetalhesDrawer ambiente={ambienteAberto} onClose={() => setAmbienteAberto(null)} />
    </Pagina>
  );
}
